
import { useState } from 'react';
import React from 'react';

interface Props {
  acreage?: number;
  cropType?: string;
  annualSavings?: number;
}

export default function LeadCaptureForm({ acreage, cropType, annualSavings }: Props) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const notes = formData.get('notes') as string;
    if (notes && notes.length > 500) {
      setStatus('idle');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d63f3t5anburc340qf50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
        setTimeout(() => setStatus('idle'), 6000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-mail-send-line text-emerald-700"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-900">Get Your Full Report</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        We&apos;ll send a detailed breakdown and schedule a no-pressure conversation about your fields.
      </p>

      <form
        data-readdy-form
        id="roi-lead-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {acreage && <input type="hidden" name="calculatedAcreage" value={acreage} />}
        {cropType && <input type="hidden" name="calculatedCrop" value={cropType} />}
        {annualSavings !== undefined && (
          <input type="hidden" name="estimatedSavings" value={annualSavings} />
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="roi-firstName"
              className="block text-sm font-bold mb-1.5"
            >
              First Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="roi-firstName"
              name="firstName"
              required
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="roi-lastName"
              className="block text-sm font-bold mb-1.5"
            >
              Last Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="roi-lastName"
              name="lastName"
              required
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="roi-email"
            className="block text-sm font-bold mb-1.5"
          >
            Email <span className="text-red-600">*</span>
          </label>
          <input
            type="email"
            id="roi-email"
            name="email"
            required
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="roi-phone"
            className="block text-sm font-bold mb-1.5"
          >
            Phone
          </label>
          <input
            type="tel"
            id="roi-phone"
            name="phone"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="roi-notes"
            className="block text-sm font-bold mb-1.5"
          >
            Anything else we should know?
          </label>
          <textarea
            id="roi-notes"
            name="notes"
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm resize-none"
            placeholder="Crop types, field challenges, timing concerns..."
          ></textarea>
          <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
        </div>

        {status === 'success' && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-sm">
            Thanks! We&apos;ll send your full report and be in touch soon.
          </div>
        )}

        {status === 'error' && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
            Something went wrong. Please try again or call us at 717-913-1780.
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {status === 'submitting' ? 'Sending...' : 'Send My Report'}
        </button>
      </form>
    </div>
  );
}
